import { Injectable } from '@angular/core';
import { InMemoryHttpClient } from './in-memory-http-client';
import { Observable } from 'rxjs';
import { RequestInfo, ResponseOptions, STATUS } from 'angular-in-memory-web-api';
import { AccessRules } from '../../../classes/enums/Authority';
import { UserGroupResponse } from '../../../interfaces/response/UserResponses';
import { CreateUserGroupRequest, EditUserGroupRolesRequest } from '../../../interfaces/request/UserRequests';

/**
 * This service intercepts {@link UserGroupsService} requests and redirects them to an in-memory data store.
 */
@Injectable({
  providedIn: 'root'
})
export class InMemoryUserGroupsDataService implements InMemoryHttpClient {

  /** User group test data. */
  userGroups: UserGroupResponse[] = [{
    email: 'scheduling_sales',
    description: 'Salesperson',
    accessRules: [AccessRules.SEE_ALL_STORES, AccessRules.SEE_PREDICTIONS, AccessRules.SEE_ROTAS]
  }, {
    email: 'scheduling_smanager',
    description: 'Store Manager',
    accessRules: [
      AccessRules.SEE_ALL_STORES, AccessRules.SEE_PREDICTIONS, AccessRules.SEE_ROTAS, AccessRules.REGENERATE_ROTAS,
      AccessRules.SEE_EMPLOYEES, AccessRules.SEE_STORE_REQUIREMENTS, AccessRules.SEE_STORE_BANKING
    ]
  }, {
    email: 'scheduling_rm',
    description: 'Area Manager',
    accessRules: [
      AccessRules.SEE_ALL_STORES, AccessRules.SEE_PREDICTIONS, AccessRules.SEE_ROTAS, AccessRules.REGENERATE_ROTAS,
      AccessRules.SEE_EMPLOYEES, AccessRules.SEE_STORE_REQUIREMENTS, AccessRules.SEE_STORE_BANKING
    ]
  }, {
    email: 'scheduling_admin',
    description: 'Administrator',
    accessRules: [
      AccessRules.SEE_ALL_STORES, AccessRules.SEE_PREDICTIONS, AccessRules.SEE_ROTAS, AccessRules.REGENERATE_ROTAS,
      AccessRules.SEE_EMPLOYEES, AccessRules.SEE_STORE_REQUIREMENTS, AccessRules.SEE_STORE_BANKING,
      AccessRules.SEE_TRADING_TIMES, AccessRules.SEE_STORE_CLOSED, AccessRules.SEE_USERS, AccessRules.MANAGE_USER_ACCESS,
      AccessRules.MANAGE_USER_ACCESS
    ]
  }];

  /**
   * Intercepts HTTP DELETE request of {@link UserGroupsService}. Deletes a record from an in-memory data store.
   * @param reqInfo information about the current HTTP DELETE request.
   */
  delete(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
      const email = reqInfo.req.url.split('user-groups/')[1].split('/')[0];
      return this.deleteUserGroup(email);
    });
  }

  /**
   * Intercepts HTTP GET request of {@link UserGroupsService}. Gets all required records from an in-memory data store.
   * @param reqInfo information about the current HTTP GET request.
   */
  get(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
      return {
        body: this.userGroups,
        status: STATUS.OK
      };
    });
  }

  /**
   * Intercepts HTTP POST request of {@link UserGroupsService}. Inserts a record to in-memory data store.
   * @param reqInfo information about the current HTTP POST request.
   */
  post(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
      const requestBody = reqInfo.utils.getJsonBody(reqInfo.req);
      return this.addUserGroup(requestBody);
    });
  }

  /**
   * Intercepts HTTP PUT request of {@link UserGroupsService}.
   * @param reqInfo information about the current HTTP PUT request.
   */
  put(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return undefined;
  }

  /**
   * Intercepts HTTP PATCH request of {@link UserGroupsService}. Updates a record in in-memory data store.
   * @param reqInfo information about the current HTTP PATCH request.
   */
  patch(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
      const requestBody = reqInfo.utils.getJsonBody(reqInfo.req);
      const email = reqInfo.req.url.split('user-groups/')[1].split('/')[0];
      return this.updateUserAccessRules(email, requestBody);
    });
  }

  private updateUserAccessRules(email: string, request: EditUserGroupRolesRequest): ResponseOptions {
    const userGroup = this.userGroups.find($userGroup => $userGroup.email === email);
    userGroup.accessRules = request.accessRules;

    return {
      status: STATUS.OK
    };
  }

  private addUserGroup(request: CreateUserGroupRequest): ResponseOptions {
    this.userGroups.push({
      email: request.email,
      description: request.description,
      accessRules: []
    });

    return {
      status: STATUS.CREATED
    };
  }

  private deleteUserGroup(email: string): ResponseOptions {
    const index = this.userGroups.findIndex(userGroup => userGroup.email === email);
    if (index !== -1) {
      this.userGroups.splice(index, 1);
    }

    return {
      status: STATUS.OK
    };
  }
}
